// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {filter, get, isEmpty, map, orderBy, pick, startCase} from 'lodash';

// Components
import BulkMapping from '../app/bulk-mapping';
import Button from '../core/button';
import Error from '../core/error';
import FlagMetadata from '../app/flag-metadata';
import Loading from '../core/loading';
import MappingDetail from '../app/mapping-detail';
import MoviesAndProgramsMappingEpisodes from './movies-and-programs-mapping-episodes';
import MoviesAndProgramsMappingFranchise from './movies-and-programs-mapping-franchise';
import Thumbnail from '../core/thumbnail';

// Containers
import FlaggedMetadata from '../../containers/flagged-metadata';
import Mapping from '../../containers/mapping';
import withPermissions from '../../containers/with-permissions';

// Context
import {AccordionContext} from '../../contexts/accordion-context';

// Styles
import './movies-and-programs-mapping.scss';

function MoviesAndProgramsMapping({ className, hasAccessByType }) {
  const getMappingButtonProps = (searchData, massShellActive) => ({
    ...pick(searchData, [
      'description',
      'descriptionLanguage',
      'echostarContentType',
      'echostarId',
      'episodeCount',
      'episodeNumber',
      'episodeSeason',
      'genres',
      'gnSeriesId',
      'originalAirDate',
      'rating',
      'releaseYear',
      'roviProgramId',
      'runtime',
      'seriesId',
      'seriesTitle',
      'sourceId',
      'titleLanguages',
      'title',
      'titleLanguages',
      'tmsId',
    ]),
    isShell: !searchData.roviProgramId && !searchData.tmsId && !searchData.sourceId,
    disabled: massShellActive,
  });

  const getClass = () => classnames(
      {
        'movies-and-programs-mapping': true,
      },
      className
        );

  const renderExitSpecialModeButton = (mapping) => {
    const { bulkMapping, cancelMapping, massShellActive, showBulkMapping } = mapping;

    return (
            <FlaggedMetadata>
                {({ roviProgramId, flaggedMetadataReset }) => (massShellActive || (bulkMapping && !showBulkMapping) || roviProgramId ? (
                        <Button
                            className="movies-and-programs-mapping--exit-button"
                            onClick={roviProgramId ? flaggedMetadataReset : cancelMapping}
                        >
                            Exit {bulkMapping ? 'Bulk Mapping' : roviProgramId ? 'Flagging' : 'Mass Shell'}
                        </Button>
                    ) : null)}
            </FlaggedMetadata>
    );
  };

  const renderFlagMetadata = (rowDetails, flaggedMetadata) => {
    const { searchData } = rowDetails;
    const { echostarId, roviProgramId, sourceId, tmsId } = flaggedMetadata;
    const dataRow = get(searchData, '0');

    return dataRow
            && echostarId
            && get(dataRow, 'echostarId') === echostarId
            && ((roviProgramId && get(dataRow, 'roviProgramId') === roviProgramId)
                || (tmsId && get(dataRow, 'tmsId') === tmsId)
                || (sourceId && get(dataRow, 'sourceId') === sourceId)) ? (
            <FlagMetadata key={searchData.roviProgramId} {...rowDetails} />
      ) : null;
  };

  const renderMoviesAndProgramsMapping = (mapping) => {
    const {
      error,
      fetched,
      fetching,
      massShellActive,
      mediaItemData,
      routerParams: { guid },
      showMessage,
    } = mapping;

    return !error && !fetched && (!fetching || massShellActive) && guid && !showMessage ? (
      !isEmpty(mediaItemData) ? (
                <FlaggedMetadata>
                    {(flaggedMetadata) => renderMoviesAndPrograms(mapping, flaggedMetadata)}
                </FlaggedMetadata>
      ) : (
                <Error message="Select a media item." />
      )
    ) : null;
  };

  const renderMoviesAndPrograms = (mapping, flaggedMetadata) => {
    const { filterByEpisodes, franchiseData, massShellActive, mediaItemData, selectTab, setFieldValues } = mapping;
    const { echostarId } = flaggedMetadata;
    const childrenProps = {
      getMappingButtonProps: (searchData) => getMappingButtonProps(searchData, massShellActive),
      renderThumbnails: renderThumbnails,
      renderDetail: (rowDetails) => renderFlagMetadata(rowDetails, flaggedMetadata),
      selectTab,
      setFieldValues,
    };
    const className = classnames(
      {
        'movies-and-programs-mapping_episodes': filterByEpisodes,
      },
      'movies-and-programs-mapping'
    );

    return (
            <AccordionContext.Provider
                value={{
                  isForceExpanded: (recordId) => echostarId && recordId === echostarId,
                }}
            >
                <span className={className}>
                    <MoviesAndProgramsMappingFranchise
                        {...childrenProps}
                        className="movies-and-programs-mapping--show"
                        mediaItemData={mediaItemData}
                    />
                    <MoviesAndProgramsMappingEpisodes
                        {...childrenProps}
                        className="movies-and-programs-mapping--episodes"
                        franchiseData={franchiseData}
                        mediaItemData={mediaItemData}
                    />
                </span>
            </AccordionContext.Provider>
    );
  };

  const renderThumbnails = (searchData) => {
    const images = orderBy(filter(get(searchData, '0.images', []), 'url'), 'category');

    return images.length ? (
            <div className="movies-and-programs-mapping--thumbnail-group">
                {map(images, ({ amsGalleryId, category, height, url, width }) => (
                    <Thumbnail
                        className="movies-and-programs-mapping--thumbnail"
                        height={height}
                        key={amsGalleryId}
                        label={startCase(category)}
                        src={url}
                        width={width}
                    />
                ))}
            </div>
    ) : null;
  };

  const renderContent = (mapping) => {
    const { showBulkMapping } = mapping;

    return showBulkMapping ? (
            <BulkMapping {...mapping} />
    ) : (
            <>
                <MappingDetail {...mapping} hasAccessByType={hasAccessByType} />
                {renderMoviesAndProgramsMapping(mapping)}
            </>
    );
  };

  return (
        <Mapping resetOnUnmount={true}>
            {(mapping) => {
              const {
                cancelMapping,
                error,
                fetching,
                routerParams: { guid },
              } = mapping;

              return guid ? (
                    <div className={getClass()}>
                        {renderExitSpecialModeButton(mapping)}
                        <Error error={error} onReset={cancelMapping} />
                        {renderContent(mapping)}
                        <Loading loading={fetching} />
                    </div>
              ) : (
                    <Error error="Select a media item." />
              );
            }}
        </Mapping>
  );
}

MoviesAndProgramsMapping.propTypes = {
    className: PropTypes.string,
    hasAccessByType: PropTypes.func.isRequired,
};

export default withPermissions(MoviesAndProgramsMapping);
